<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useMainStore } from '@/stores/main'
import { useUserStore } from '@/stores/user'
import Signal from '@/api/Signal'
import SignalDetail from '@/components/Signals/SignalDetail.vue'
import CreateSignal from '@/components/Signals/CreateSignal.vue'
import FeedManagement from '@/components/Signals/FeedManagement.vue'
import SignalQueue from '@/components/Signals/SignalQueue.vue'
import SuggestFeed from '@/components/Signals/SuggestFeed.vue'
import SourceSelector from '@/components/common/SourceSelector.vue'
import PageToolbar from '@/components/common/PageToolbar.vue'
import ToolbarHeading from '@/components/common/ToolbarHeading.vue'
import { formatRelativeDate, formatCount } from '@/lib/formatDate'

const toast = useToast()
const mainStore = useMainStore()
const userStore = useUserStore()

const signals = ref([])
const total = ref(0)
const loading = ref(true)
const loadingMore = ref(false)
const error = ref('')
const deletingId = ref(null)
const markingIrrelevantId = ref(null)
const retryingId = ref(null)
const limit = 20
const activeFilter = ref('all')
const activeSort = ref('created')
const searchQuery = ref('')
const debouncedSearch = ref('')
const queueCount = ref(0)
const brokenImages = ref(new Set())
const sourceFilter = ref({ sources: [], logTypes: [] })

// Debounce search input
let searchTimer = null
watch(searchQuery, (val) => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    debouncedSearch.value = val.trim()
  }, 300)
})

const LAYOUT_KEY = 'webscout-signals-layout'
const activeLayout = ref(localStorage.getItem(LAYOUT_KEY) || 'grid')
watch(activeLayout, (val) => localStorage.setItem(LAYOUT_KEY, val))

const isAdmin = computed(() => userStore.accountType === 1337)
const hasMore = ref(false)

// Data fetching
const listOpts = computed(() => ({
  sort: activeSort.value,
  pinned: activeFilter.value === 'pinned',
  search: debouncedSearch.value || undefined,
  sources: sourceFilter.value?.sources?.length ? sourceFilter.value.sources : undefined,
}))

async function loadSignals(initial = false) {
  if (initial) loading.value = true
  error.value = ''
  try {
    const res = await Signal.list(0, limit, listOpts.value)
    if (res.status === 'success') {
      signals.value = res.data.signals || []
      total.value = res.data.total || 0
      hasMore.value = signals.value.length < total.value
    } else {
      error.value = res.message || 'Failed to load signals'
    }
  } catch {
    error.value = 'An unexpected error occurred'
  } finally {
    loading.value = false
  }
}

async function loadMore() {
  if (loadingMore.value || !hasMore.value) return
  loadingMore.value = true
  try {
    const res = await Signal.list(signals.value.length, limit, listOpts.value)
    if (res.status === 'success') {
      const newSignals = res.data.signals || []
      signals.value.push(...newSignals)
      hasMore.value = signals.value.length < (res.data.total || 0)
    } else {
      hasMore.value = false
    }
  } catch {
    hasMore.value = false
  } finally {
    loadingMore.value = false
  }
  await nextTick()
  checkSentinelVisible()
}

function checkSentinelVisible() {
  if (!sentinel.value || !hasMore.value || loadingMore.value) return
  const rect = sentinel.value.getBoundingClientRect()
  const viewportHeight = window.innerHeight
  if (rect.top < viewportHeight + 200) {
    loadMore()
  }
}

async function loadQueueCount() {
  try {
    const res = await Signal.listQueue()
    if (res.status === 'success') {
      const sigs = res.data.signals || []
      queueCount.value = sigs.filter(s => s.status === 0 || s.status === 1).length
    }
  } catch {
    // Silently fail — queue count is non-critical
  }
}

// Watch filter/sort/search/source changes
watch([activeFilter, activeSort, debouncedSearch, sourceFilter], async () => {
  await loadSignals()
  await nextTick()
  checkSentinelVisible()
})

async function togglePin(s) {
  const wasPinned = s.pinned
  s.pinned = !wasPinned
  const res = wasPinned ? await Signal.unpin(s.id) : await Signal.pin(s.id)
  if (res.status !== 'success') {
    s.pinned = wasPinned
    toast.error(res.message || 'Failed to update pin')
    return
  }
  // If viewing pinned filter and we just unpinned, remove from list
  if (activeFilter.value === 'pinned' && wasPinned) {
    signals.value = signals.value.filter(sig => sig.id !== s.id)
    total.value--
  }
}

// Infinite scroll
let observer = null
const sentinel = ref(null)

function setupObserver() {
  if (!sentinel.value) return
  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && hasMore.value && !loadingMore.value) {
      loadMore()
    }
  }, { rootMargin: '200px' })
  observer.observe(sentinel.value)
}

// Actions
function onPinChanged({ id, pinned }) {
  const idx = signals.value.findIndex(s => s.id === id)
  if (idx !== -1) {
    signals.value[idx].pinned = pinned
    // If viewing pinned filter and unpinned, remove from list
    if (activeFilter.value === 'pinned' && !pinned) {
      signals.value.splice(idx, 1)
      total.value--
    }
  }
}

function openDetail(s) {
  mainStore.show_slideover(
    SignalDetail,
    { signalId: s.id, onPinChanged },
    { width: 50 },
    s.title || 'Signal'
  )
}

function openCreate() {
  mainStore.show_modal(CreateSignal, { onCreated: onSignalCreated })
}

function openFeeds() {
  mainStore.show_slideover(FeedManagement, {}, { width: 50 }, 'Manage Feeds')
}

function openQueue() {
  mainStore.show_slideover(
    SignalQueue,
    { onQueueChanged: (count) => { queueCount.value = count } },
    { width: 50 },
    'Signal Queue'
  )
}

function openSuggest() {
  mainStore.show_modal(SuggestFeed, { onSuggested: () => {} })
}

function onSignalCreated() {
  queueCount.value++
  toast.info('Signal queued for processing')
}

async function deleteSignal(id) {
  if (deletingId.value) return
  deletingId.value = id
  try {
    const res = await Signal.delete(id)
    if (res.status === 'success') {
      signals.value = signals.value.filter(s => s.id !== id)
      total.value--
      toast.success('Signal deleted')
    } else {
      toast.error(res.message || 'Failed to delete signal')
    }
  } finally {
    deletingId.value = null
  }
}

async function markIrrelevant(id) {
  if (markingIrrelevantId.value) return
  markingIrrelevantId.value = id
  try {
    const res = await Signal.markIrrelevant(id)
    if (res.status === 'success') {
      signals.value = signals.value.filter(s => s.id !== id)
      total.value--
      toast.success('Signal marked as irrelevant')
    } else {
      toast.error(res.message || 'Failed to mark signal')
    }
  } finally {
    markingIrrelevantId.value = null
  }
}

async function retrySignal(id) {
  if (retryingId.value) return
  retryingId.value = id
  try {
    const res = await Signal.retry(id)
    if (res.status === 'success') {
      signals.value = signals.value.filter(s => s.id !== id)
      total.value--
      toast.success('Signal queued for reanalysis')
    } else {
      toast.error(res.message || 'Failed to retry signal')
    }
  } finally {
    retryingId.value = null
  }
}

function onImageError(id) {
  brokenImages.value.add(id)
}

function isSafeURL(url) {
  if (!url) return false
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

onMounted(async () => {
  await loadSignals(true)
  setupObserver()
  await nextTick()
  checkSentinelVisible()
  if (isAdmin.value) loadQueueCount()
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<template>
  <div class="signals-page">
    <div class="signals-inner">
      <!-- Toolbar -->
      <PageToolbar sticky>
          <ToolbarHeading
            title="Signals"
            subtitle="Curated threat-intelligence signals from your feeds"
            :count="!loading && total > 0 ? total : null"
          />

          <div class="toolbar-controls">
          <div class="header-tabs">
            <button
              class="tab-btn"
              :class="{ active: activeFilter === 'all' }"
              @click="activeFilter = 'all'"
            >
              <font-awesome-icon :icon="['fas', 'bars']" />
              All
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeFilter === 'pinned' }"
              @click="activeFilter = 'pinned'"
            >
              <font-awesome-icon :icon="['fas', 'thumbtack']" />
              Pinned
            </button>
          </div>

          <div class="header-tabs">
            <button
              class="tab-btn"
              :class="{ active: activeSort === 'created' }"
              @click="activeSort = 'created'"
            >
              <font-awesome-icon :icon="['fas', 'clock']" />
              Added
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeSort === 'published' }"
              @click="activeSort = 'published'"
            >
              <font-awesome-icon :icon="['fas', 'calendar']" />
              Published
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeSort === 'activity' }"
              @click="activeSort = 'activity'"
            >
              <font-awesome-icon :icon="['fas', 'crosshairs']" />
              Hits
            </button>
          </div>

          <div class="toolbar-search-box">
            <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="search-icon" />
            <input
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="Search signals..."
            />
            <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </div>

          <SourceSelector mode="local" v-model="sourceFilter" :show-log-types="false" />

          <div class="header-tabs layout-tabs">
            <button class="tab-btn layout-btn" :class="{ active: activeLayout === 'grid' }"
              @click="activeLayout = 'grid'" title="Grid view">
              <font-awesome-icon :icon="['fas', 'grip']" />
            </button>
            <button class="tab-btn layout-btn" :class="{ active: activeLayout === 'list' }"
              @click="activeLayout = 'list'" title="List view">
              <font-awesome-icon :icon="['fas', 'list']" />
            </button>
            <button class="tab-btn layout-btn" :class="{ active: activeLayout === 'card' }"
              @click="activeLayout = 'card'" title="Card view">
              <font-awesome-icon :icon="['fas', 'table-list']" />
            </button>
          </div>

          <button v-if="!isAdmin" class="btn-header" @click="openSuggest">
            <font-awesome-icon :icon="['fas', 'rss']" />
            Suggest Feed
          </button>
          <template v-if="isAdmin">
            <button class="btn-header" @click="openFeeds">
              <font-awesome-icon :icon="['fas', 'rss']" />
              Feeds
            </button>
            <button class="btn-header" @click="openQueue">
              <font-awesome-icon :icon="['fas', 'layer-group']" />
              Queue
              <span v-if="queueCount > 0" class="queue-badge">{{ queueCount }}</span>
            </button>
            <button class="btn-create" @click="openCreate">
              <font-awesome-icon :icon="['fas', 'plus']" />
              Create
            </button>
          </template>
          </div>
        </PageToolbar>

      <!-- Loading -->
      <div v-if="loading" class="loading-state">
        <font-awesome-icon :icon="['fas', 'spinner']" spin class="loading-icon" />
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="error-state">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="error-icon" />
        <h3>Something went wrong</h3>
        <p>{{ error }}</p>
        <button class="btn-retry" @click="loadSignals(true)">Try again</button>
      </div>

      <!-- Empty state -->
      <div v-else-if="signals.length === 0" class="empty-state">
        <font-awesome-icon :icon="debouncedSearch ? ['fas', 'magnifying-glass'] : activeFilter === 'pinned' ? ['fas', 'thumbtack'] : ['fas', 'satellite-dish']" class="empty-icon" />
        <h3>{{ debouncedSearch ? 'No matching signals' : activeFilter === 'pinned' ? 'No pinned signals' : 'No signals yet' }}</h3>
        <p>{{ debouncedSearch ? 'Try a different search term.' : activeFilter === 'pinned' ? 'Pin signals to save them here for quick access.' : 'Threat intelligence signals will appear here once created.' }}</p>
      </div>

      <!-- Signals container -->
      <div v-else :class="['signals-container', 'layout-' + activeLayout]">
        <template v-for="s in signals" :key="s.id">

          <!-- GRID layout -->
          <div v-if="activeLayout === 'grid'" class="signal-card" @click="openDetail(s)">
            <div class="card-image">
              <img
                v-if="isSafeURL(s.image_url) && !brokenImages.has(s.id)"
                :src="s.image_url"
                :alt="s.title"
                @error="onImageError(s.id)"
              />
              <div v-else class="card-image-placeholder">
                <font-awesome-icon :icon="['fas', 'satellite-dish']" />
              </div>
            </div>
            <div class="card-body">
              <h3 class="card-title">{{ s.title || 'Processing...' }}</h3>
              <p v-if="s.summary" class="card-summary">{{ s.summary }}</p>
              <div class="card-meta">
                <div class="meta-left">
                  <span v-if="s.ioc_count > 0" class="meta-badge">
                    <font-awesome-icon :icon="['fas', 'shield-alt']" />
                    {{ formatCount(s.ioc_count) }} IOC{{ s.ioc_count !== 1 ? 's' : '' }}
                  </span>
                  <span v-if="s.result_count > 0" class="meta-badge hits">
                    <font-awesome-icon :icon="['fas', 'crosshairs']" />
                    {{ formatCount(s.result_count) }} hit{{ s.result_count !== 1 ? 's' : '' }}
                  </span>
                  <span v-if="s.source_type === 3" class="meta-badge source-feed" title="From RSS feed">
                    <font-awesome-icon :icon="['fas', 'rss']" />
                  </span>
                  <span v-else class="meta-badge source-manual" title="Manually added">
                    <font-awesome-icon :icon="['fas', 'user']" />
                  </span>
                  <span class="meta-date">
                    {{ activeSort === 'activity'
                      ? (s.last_activity_at ? formatRelativeDate(s.last_activity_at) : 'No hits')
                      : activeSort === 'published'
                        ? (s.published_at ? formatRelativeDate(s.published_at) : 'Unknown')
                        : formatRelativeDate(s.created_at) }}
                  </span>
                </div>
                <div class="meta-right">
                  <button class="btn-pin" :class="{ pinned: s.pinned }" @click.stop="togglePin(s)" :title="s.pinned ? 'Unpin' : 'Pin'">
                    <font-awesome-icon :icon="['fas', 'thumbtack']" />
                  </button>
                  <button v-if="isAdmin" class="btn-reanalyze" @click.stop="retrySignal(s.id)" :disabled="retryingId === s.id" title="Reanalyze">
                    <font-awesome-icon v-if="retryingId === s.id" :icon="['fas', 'spinner']" spin />
                    <font-awesome-icon v-else :icon="['fas', 'arrow-rotate-right']" />
                  </button>
                  <button v-if="isAdmin" class="btn-irrelevant" @click.stop="markIrrelevant(s.id)" :disabled="markingIrrelevantId === s.id" title="Mark irrelevant">
                    <font-awesome-icon v-if="markingIrrelevantId === s.id" :icon="['fas', 'spinner']" spin />
                    <font-awesome-icon v-else :icon="['fas', 'eye-slash']" />
                  </button>
                  <button v-if="isAdmin" class="btn-delete" @click.stop="deleteSignal(s.id)" :disabled="deletingId === s.id" title="Delete signal">
                    <font-awesome-icon v-if="deletingId === s.id" :icon="['fas', 'spinner']" spin />
                    <font-awesome-icon v-else :icon="['fas', 'trash']" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- LIST layout -->
          <div v-else-if="activeLayout === 'list'" class="signal-list-row" @click="openDetail(s)">
            <span class="list-title">{{ s.title || 'Processing...' }}</span>
            <span class="list-col list-col-ioc">
              <span v-if="s.ioc_count > 0" class="meta-badge">
                <font-awesome-icon :icon="['fas', 'shield-alt']" />
                {{ formatCount(s.ioc_count) }}
              </span>
            </span>
            <span class="list-col list-col-hits">
              <span v-if="s.result_count > 0" class="meta-badge hits">
                <font-awesome-icon :icon="['fas', 'crosshairs']" />
                {{ formatCount(s.result_count) }}
              </span>
            </span>
            <span class="list-col list-col-rss">
              <span v-if="s.source_type === 3" class="meta-badge source-feed" title="From RSS feed">
                <font-awesome-icon :icon="['fas', 'rss']" />
              </span>
              <span v-else class="meta-badge source-manual" title="Manually added">
                <font-awesome-icon :icon="['fas', 'user']" />
              </span>
            </span>
            <span class="list-col list-col-date">
              {{ activeSort === 'activity'
                ? (s.last_activity_at ? formatRelativeDate(s.last_activity_at) : 'Never')
                : activeSort === 'published'
                  ? (s.published_at ? formatRelativeDate(s.published_at) : 'Unknown')
                  : formatRelativeDate(s.created_at) }}
            </span>
            <div class="list-actions">
              <button class="btn-pin" :class="{ pinned: s.pinned }" @click.stop="togglePin(s)" :title="s.pinned ? 'Unpin' : 'Pin'">
                <font-awesome-icon :icon="['fas', 'thumbtack']" />
              </button>
              <button v-if="isAdmin" class="btn-reanalyze" @click.stop="retrySignal(s.id)" :disabled="retryingId === s.id" title="Reanalyze">
                <font-awesome-icon v-if="retryingId === s.id" :icon="['fas', 'spinner']" spin />
                <font-awesome-icon v-else :icon="['fas', 'arrow-rotate-right']" />
              </button>
              <button v-if="isAdmin" class="btn-irrelevant" @click.stop="markIrrelevant(s.id)" :disabled="markingIrrelevantId === s.id" title="Mark irrelevant">
                <font-awesome-icon v-if="markingIrrelevantId === s.id" :icon="['fas', 'spinner']" spin />
                <font-awesome-icon v-else :icon="['fas', 'eye-slash']" />
              </button>
              <button v-if="isAdmin" class="btn-delete" @click.stop="deleteSignal(s.id)" :disabled="deletingId === s.id" title="Delete signal">
                <font-awesome-icon v-if="deletingId === s.id" :icon="['fas', 'spinner']" spin />
                <font-awesome-icon v-else :icon="['fas', 'trash']" />
              </button>
            </div>
          </div>

          <!-- CARD (large) layout -->
          <div v-else class="signal-card-large" @click="openDetail(s)">
            <div class="card-large-image">
              <img
                v-if="isSafeURL(s.image_url) && !brokenImages.has(s.id)"
                :src="s.image_url"
                :alt="s.title"
                @error="onImageError(s.id)"
              />
              <div v-else class="card-image-placeholder">
                <font-awesome-icon :icon="['fas', 'satellite-dish']" />
              </div>
            </div>
            <div class="card-large-body">
              <h3 class="card-large-title">{{ s.title || 'Processing...' }}</h3>
              <div v-if="s.summary" class="card-large-summary-wrap">
                <p class="card-large-summary">{{ s.summary }}</p>
              </div>
              <div class="card-meta">
                <div class="meta-left">
                  <span v-if="s.ioc_count > 0" class="meta-badge">
                    <font-awesome-icon :icon="['fas', 'shield-alt']" />
                    {{ formatCount(s.ioc_count) }} IOC{{ s.ioc_count !== 1 ? 's' : '' }}
                  </span>
                  <span v-if="s.result_count > 0" class="meta-badge hits">
                    <font-awesome-icon :icon="['fas', 'crosshairs']" />
                    {{ formatCount(s.result_count) }} hit{{ s.result_count !== 1 ? 's' : '' }}
                  </span>
                  <span v-if="s.source_type === 3" class="meta-badge source-feed" title="From RSS feed">
                    <font-awesome-icon :icon="['fas', 'rss']" />
                  </span>
                  <span v-else class="meta-badge source-manual" title="Manually added">
                    <font-awesome-icon :icon="['fas', 'user']" />
                  </span>
                  <span class="meta-date">
                    {{ activeSort === 'activity'
                      ? (s.last_activity_at ? formatRelativeDate(s.last_activity_at) : 'No hits')
                      : activeSort === 'published'
                        ? (s.published_at ? formatRelativeDate(s.published_at) : 'Unknown')
                        : formatRelativeDate(s.created_at) }}
                  </span>
                </div>
                <div class="meta-right">
                  <button class="btn-pin" :class="{ pinned: s.pinned }" @click.stop="togglePin(s)" :title="s.pinned ? 'Unpin' : 'Pin'">
                    <font-awesome-icon :icon="['fas', 'thumbtack']" />
                  </button>
                  <button v-if="isAdmin" class="btn-reanalyze" @click.stop="retrySignal(s.id)" :disabled="retryingId === s.id" title="Reanalyze">
                    <font-awesome-icon v-if="retryingId === s.id" :icon="['fas', 'spinner']" spin />
                    <font-awesome-icon v-else :icon="['fas', 'arrow-rotate-right']" />
                  </button>
                  <button v-if="isAdmin" class="btn-irrelevant" @click.stop="markIrrelevant(s.id)" :disabled="markingIrrelevantId === s.id" title="Mark irrelevant">
                    <font-awesome-icon v-if="markingIrrelevantId === s.id" :icon="['fas', 'spinner']" spin />
                    <font-awesome-icon v-else :icon="['fas', 'eye-slash']" />
                  </button>
                  <button v-if="isAdmin" class="btn-delete" @click.stop="deleteSignal(s.id)" :disabled="deletingId === s.id" title="Delete signal">
                    <font-awesome-icon v-if="deletingId === s.id" :icon="['fas', 'spinner']" spin />
                    <font-awesome-icon v-else :icon="['fas', 'trash']" />
                  </button>
                </div>
              </div>
            </div>
          </div>

        </template>
      </div>

      <!-- Sentinel for infinite scroll -->
      <div ref="sentinel" class="scroll-sentinel">
        <font-awesome-icon v-if="loadingMore" :icon="['fas', 'spinner']" spin class="loading-more" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.signals-page {
  width: 100%;
  height: 100%;
  background: var(--surface-page);
  overflow-y: auto;
}

/* Full-bleed page: flush-left against the sidebar, no max-width cap.
   #main-scroll-container supplies the 1rem top/right/bottom inset. */
.signals-inner {
  padding: 0 0 2rem 0;
  width: 100%;
}

/* Toolbar shell + slot atoms (.page-header, .toolbar-sticky, .header-tabs,
   .toolbar-spacer, .toolbar-search-box, .tab-btn, .btn-header, .btn-create)
   live inside <PageToolbar/>. .dropdown-trigger is global via
   @/styles/dropdown-trigger.css. The title/count live in <ToolbarHeading/>. */

/* Full-bleed: the sticky wrapper no longer needs to cancel side padding. */
:deep(.toolbar-sticky) {
  margin: 0;
  padding: 0 0 1.25rem;
}

/* The interactive controls center as a group within the taller bar and are
   right-aligned so only the title/subtitle sit on the left; every control
   clusters on the right edge. */
.toolbar-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

/* Don't let the search box grab the free space (its default flex: 1 would
   fill the row and defeat the right alignment); size it to content instead. */
.toolbar-controls :deep(.toolbar-search-box) {
  flex: 0 1 240px;
}

.queue-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.125rem;
  height: 1.125rem;
  padding: 0 0.3rem;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
  color: var(--text-heading);
  background: rgba(115, 87, 210, 0.6);
  border-radius: var(--radius-md);
}

/* Pin button on card */
.btn-pin {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-disabled);
  cursor: pointer;
  font-size: 0.6875rem;
  transition: var(--transition-fast);
}

.btn-pin:hover {
  color: var(--accent-text);
  background: var(--accent-soft-3);
}

.btn-pin.pinned {
  color: var(--accent-text);
}

/* Loading */
.loading-state {
  display: flex;
  justify-content: center;
  padding: 4rem;
  color: var(--text-faint);
  font-size: 1.5rem;
}

/* Error */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 1rem;
  color: var(--text-faint);
  text-align: center;
}

.error-icon {
  font-size: 2.5rem;
  color: var(--color-error);
  opacity: 0.5;
  margin-bottom: 1rem;
}

.error-state h3 {
  font-size: 1.125rem;
  color: var(--text-muted);
  margin: 0 0 0.5rem 0;
}

.error-state p {
  font-size: 0.875rem;
  margin: 0 0 1rem 0;
}

.btn-retry {
  padding: 0.5rem 1rem;
  background: var(--surface-overlay-3);
  border: none;
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn-retry:hover {
  background: rgba(255, 255, 255, 0.12);
}

/* Empty */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 1rem;
  color: var(--text-faint);
  text-align: center;
}

.empty-icon {
  font-size: 2.5rem;
  opacity: 0.3;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.125rem;
  color: var(--text-muted);
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  font-size: 0.875rem;
  margin: 0;
}

/* Layout toggle — icon-only tabs. Inherit everything from .tab-btn; only
   tighten horizontal padding since there's no text label. */
.layout-tabs { margin-left: 0; }
.layout-btn { padding: 0.5rem 0.75rem; }

/* Layout containers */
.layout-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.25rem;
}

.layout-list {
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: var(--surface-overlay);
  border-radius: var(--radius-xl);
  overflow: hidden;
}

.layout-card {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* Card */
.signal-card {
  background: var(--surface-card);
  border: none;
  border-radius: var(--radius-xl);
  overflow: hidden;
  cursor: pointer;
  transition: var(--transition);
}

.signal-card:hover {
  background: var(--surface-overlay-2);
}

.card-image {
  width: 100%;
  height: 180px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.25);
  font-size: 3rem;
}

.card-body {
  padding: 1rem 1.25rem 1.25rem;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-summary {
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 0 0 0.75rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Meta row */
.card-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.meta-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  min-width: 0;
}

.meta-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.meta-badge {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--surface-overlay-2);
  padding: 0.1875rem 0.5rem;
  border-radius: var(--radius-xs);
}

.meta-badge.hits {
  color: var(--accent-text);
  background: var(--accent-soft-2);
}

.meta-badge.source-feed {
  color: var(--color-feed);
  background: var(--color-feed-soft);
  font-size: 0.625rem;
  padding: 0.1875rem 0.375rem;
}

.meta-badge.source-manual {
  color: var(--color-info-alt);
  background: var(--color-info-alt-soft);
  font-size: 0.625rem;
  padding: 0.1875rem 0.375rem;
}

.meta-date {
  font-size: 0.6875rem;
  color: var(--text-primary);
}

/* Card action buttons */
.btn-reanalyze,
.btn-irrelevant,
.btn-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-disabled);
  cursor: pointer;
  font-size: 0.6875rem;
  transition: var(--transition-fast);
}

.btn-reanalyze:hover:not(:disabled) {
  color: var(--color-info);
  background: var(--color-info-soft);
}

.btn-irrelevant:hover:not(:disabled) {
  color: var(--color-warning);
  background: var(--color-warning-soft);
}

.btn-delete:hover:not(:disabled) {
  color: var(--color-error);
  background: var(--color-error-soft);
}

.btn-reanalyze:disabled,
.btn-irrelevant:disabled,
.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* List row — CSS grid for table-like column alignment */
.signal-list-row {
  display: grid;
  grid-template-columns: 1fr 4.5rem 4.5rem 1.75rem 5.5rem auto;
  align-items: center;
  gap: 0 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--surface-card);
  cursor: pointer;
  transition: background 0.15s ease;
}

.signal-list-row:hover {
  background: var(--surface-overlay-2);
}

.list-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.list-col {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.list-col-date {
  font-size: 0.6875rem;
  color: var(--text-primary);
  justify-content: flex-end;
  white-space: nowrap;
}

.list-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  justify-content: flex-end;
}

/* Card large */
.signal-card-large {
  display: flex;
  background: var(--surface-card);
  border-radius: var(--radius-xl);
  overflow: hidden;
  cursor: pointer;
  transition: var(--transition);
}

.signal-card-large:hover {
  background: var(--surface-overlay-2);
}

.card-large-image {
  width: 280px;
  height: 200px;
  flex-shrink: 0;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
}

.card-large-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-large-body {
  flex: 1;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.card-large-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-large-summary-wrap {
  margin: 0 0 auto 0;
  padding-bottom: 0.75rem;
  min-height: 0;
  overflow: hidden;
}

.card-large-summary {
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: break-word;
  word-break: break-word;
}

@media (max-width: 640px) {
  .signal-card-large {
    flex-direction: column;
  }
  .card-large-image {
    width: 100%;
    height: 180px;
    min-height: auto;
  }
}

/* Sentinel */
.scroll-sentinel {
  display: flex;
  justify-content: center;
  padding: 2rem;
  min-height: 1px;
}

.loading-more {
  color: var(--text-faint);
  font-size: 1.25rem;
}
</style>
